import { DASHBOARD_SCHEMA_VERSION, DASHBOARD_WIDGET_IDS } from '@zeroth/shared';

import type { DashboardData } from '../hooks/useDashboard';

export interface DashboardApiResponse {
  schemaVersion: number;
  data: unknown;
}

export class DashboardDataContractError extends Error {
  constructor(readonly reason: string) {
    super(`Dashboard response is incompatible: ${reason}`);
    this.name = 'DashboardDataContractError';
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function fail(path: string, expectation: string): never {
  throw new DashboardDataContractError(`${path} ${expectation}`);
}

function requireRecord(
  parent: Record<string, unknown>,
  key: string,
  path = key,
): Record<string, unknown> {
  const value = parent[key];
  return isRecord(value) ? value : fail(path, 'is missing');
}

function requireArray(parent: Record<string, unknown>, key: string, path = key): unknown[] {
  const value = parent[key];
  return Array.isArray(value) ? value : fail(path, 'is not an array');
}

function requireString(parent: Record<string, unknown>, key: string, path = key): void {
  if (typeof parent[key] !== 'string') fail(path, 'is not a string');
}

function requireNumber(parent: Record<string, unknown>, key: string, path = key): void {
  const value = parent[key];
  if (typeof value !== 'number' || !Number.isFinite(value)) fail(path, 'is not a finite number');
}

function requireBoolean(parent: Record<string, unknown>, key: string, path = key): void {
  if (typeof parent[key] !== 'boolean') fail(path, 'is not a boolean');
}

function requireNullableString(parent: Record<string, unknown>, key: string, path = key): void {
  const value = parent[key];
  if (value !== null && typeof value !== 'string') fail(path, 'is not a string or null');
}

function requireNullableNumber(parent: Record<string, unknown>, key: string, path = key): void {
  const value = parent[key];
  if (value !== null && (typeof value !== 'number' || !Number.isFinite(value))) {
    fail(path, 'is not a finite number or null');
  }
}

function requireOptionalString(parent: Record<string, unknown>, key: string, path = key): void {
  const value = parent[key];
  if (value !== undefined && typeof value !== 'string') fail(path, 'is not a string');
}

function requireEnum(
  parent: Record<string, unknown>,
  key: string,
  values: readonly string[],
  path = key,
): void {
  if (typeof parent[key] !== 'string' || !values.includes(parent[key])) fail(path, 'is invalid');
}

function requireNullableEnum(
  parent: Record<string, unknown>,
  key: string,
  values: readonly string[],
  path = key,
): void {
  const value = parent[key];
  if (value !== null && (typeof value !== 'string' || !values.includes(value))) {
    fail(path, 'is invalid');
  }
}

function requireStringArray(parent: Record<string, unknown>, key: string, path = key): string[] {
  const values = requireArray(parent, key, path);
  if (!values.every((value) => typeof value === 'string')) fail(path, 'contains an invalid value');
  return values;
}

function requireRecordArray(
  parent: Record<string, unknown>,
  key: string,
  validate: (record: Record<string, unknown>, path: string) => void,
  path = key,
): void {
  requireArray(parent, key, path).forEach((value, index) => {
    if (!isRecord(value)) fail(`${path}[${index}]`, 'is not an object');
    validate(value, `${path}[${index}]`);
  });
}

function validateRange(range: Record<string, unknown>): void {
  requireEnum(range, 'kind', ['preset', 'custom'], 'range.kind');
  requireNullableEnum(range, 'preset', ['7d', '30d', '60d', '90d'], 'range.preset');
  for (const key of ['startDate', 'endDate', 'label', 'timezone', 'minDate', 'maxDate']) {
    requireString(range, key, `range.${key}`);
  }
  requireNumber(range, 'days', 'range.days');
}

function validateKpis(kpis: Record<string, unknown>): void {
  const inventory = requireRecord(kpis, 'inventory', 'kpis.inventory');
  for (const key of [
    'totalSKUs', 'activeSKUs', 'totalFBAUnits', 'totalInboundUnits', 'totalLocalUnits',
    'totalOnOrderUnits', 'estimatedValue',
  ]) requireNumber(inventory, key, `kpis.inventory.${key}`);

  const health = requireRecord(kpis, 'health', 'kpis.health');
  for (const key of ['lowStockCount', 'outOfStockCount', 'healthyCount']) {
    requireNumber(health, key, `kpis.health.${key}`);
  }

  const restock = requireRecord(kpis, 'restock', 'kpis.restock');
  requireNumber(restock, 'redAlerts', 'kpis.restock.redAlerts');
  requireNumber(restock, 'yellowAlerts', 'kpis.restock.yellowAlerts');
  requireNullableNumber(restock, 'totalRestockValue', 'kpis.restock.totalRestockValue');
  requireEnum(restock, 'valueStatus', ['COMPLETE', 'PARTIAL', 'UNAVAILABLE'], 'kpis.restock.valueStatus');
  requireNumber(restock, 'unpricedProductCount', 'kpis.restock.unpricedProductCount');

  const purchaseOrders = requireRecord(kpis, 'purchaseOrders', 'kpis.purchaseOrders');
  for (const key of ['draftCount', 'submittedCount', 'partialCount', 'totalPendingValue']) {
    requireNumber(purchaseOrders, key, `kpis.purchaseOrders.${key}`);
  }
}

function validateCharts(charts: Record<string, unknown>): void {
  const salesMetrics = requireRecord(charts, 'salesMetrics', 'charts.salesMetrics');
  requireEnum(salesMetrics, 'status', ['ready', 'pending'], 'charts.salesMetrics.status');
  const salesMetricKeys = [
    'totalRevenue', 'totalUnits', 'totalOrderItems', 'avgDailyRevenue', 'avgDailyUnits',
    'avgSalesPerOrderItem',
  ];
  for (const key of salesMetricKeys) {
    if (salesMetrics.status === 'ready') requireNumber(salesMetrics, key, `charts.salesMetrics.${key}`);
    else if (salesMetrics[key] !== null) fail(`charts.salesMetrics.${key}`, 'must be null while pending');
  }

  const inventoryDistribution = requireRecord(
    charts,
    'inventoryDistribution',
    'charts.inventoryDistribution',
  );
  for (const key of ['inStock', 'lowStock', 'outOfStock']) {
    requireNumber(inventoryDistribution, key, `charts.inventoryDistribution.${key}`);
  }

  requireRecordArray(charts, 'topProductsByValue', (point, path) => {
    requireString(point, 'label', `${path}.label`);
    requireNumber(point, 'value', `${path}.value`);
    requireOptionalString(point, 'title', `${path}.title`);
    requireOptionalString(point, 'imageUrl', `${path}.imageUrl`);
  }, 'charts.topProductsByValue');
}

function validateTopProduct(product: Record<string, unknown>, path: string): void {
  for (const key of ['id', 'sku', 'title']) requireString(product, key, `${path}.${key}`);
  requireNullableString(product, 'imageUrl', `${path}.imageUrl`);
  for (const key of ['velocity', 'totalRevenue', 'fbaQuantity', 'daysOfStock']) {
    requireNumber(product, key, `${path}.${key}`);
  }
}

function validateRestockAlert(alert: Record<string, unknown>, path: string): void {
  for (const key of ['id', 'sku', 'title']) requireString(alert, key, `${path}.${key}`);
  requireNullableString(alert, 'imageUrl', `${path}.imageUrl`);
  for (const key of ['fbaQuantity', 'velocity30d', 'daysOfStock']) {
    requireNumber(alert, key, `${path}.${key}`);
  }
  requireEnum(alert, 'urgency', ['RED', 'YELLOW'], `${path}.urgency`);
  requireNullableString(alert, 'supplierId', `${path}.supplierId`);
  requireNullableString(alert, 'supplierName', `${path}.supplierName`);
  requireNullableEnum(
    alert,
    'profitabilitySetupTarget',
    ['PRODUCT_COST', 'PRICING_DATA'],
    `${path}.profitabilitySetupTarget`,
  );
  const decision = requireRecord(alert, 'purchaseDecision', `${path}.purchaseDecision`);
  requireEnum(decision, 'action', ['BUY', 'REVIEW', 'HOLD'], `${path}.purchaseDecision.action`);
  const reasonCodes = requireStringArray(
    decision,
    'reasonCodes',
    `${path}.purchaseDecision.reasonCodes`,
  );
  const acceptedReasonCodes = [
    'ALL_GUARDRAILS_PASSED', 'NO_PURCHASE_QUANTITY', 'CURRENT_PRICE_UNPROFITABLE',
    'MISSING_CURRENT_PROFIT', 'MISSING_SUGGESTED_MARGIN', 'MISSING_SUGGESTED_ROI',
    'MISSING_COMPETITIVE_OFFERS', 'MISSING_BUY_BOX_SHARE', 'MISSING_COMPETITION_CAPTURE_TIME',
    'MISSING_PRICE_SUGGESTION', 'STALE_COMPETITION_DATA', 'MEDIUM_PRICE_CONFIDENCE',
    'LOW_PRICE_CONFIDENCE', 'MARGIN_BELOW_MINIMUM', 'ROI_BELOW_MINIMUM',
    'TOO_MANY_COMPETITIVE_OFFERS', 'BUY_BOX_SHARE_BELOW_MINIMUM', 'PRICE_FLOOR_BLOCKED',
  ];
  if (!reasonCodes.every((reasonCode) => acceptedReasonCodes.includes(reasonCode))) {
    fail(`${path}.purchaseDecision.reasonCodes`, 'contains an invalid value');
  }
}

function validateTopRestockProduct(product: Record<string, unknown>, path: string): void {
  for (const key of ['id', 'sku', 'title']) requireString(product, key, `${path}.${key}`);
  requireNullableString(product, 'imageUrl', `${path}.imageUrl`);
  requireNullableString(product, 'supplierName', `${path}.supplierName`);
  for (const key of ['fbaQuantity', 'daysOfStock', 'suggestedQty']) {
    requireNumber(product, key, `${path}.${key}`);
  }
  requireNullableNumber(product, 'estimatedValue', `${path}.estimatedValue`);
  requireEnum(product, 'urgency', ['RED', 'YELLOW', 'NONE'], `${path}.urgency`);
}

function validatePurchaseOrder(order: Record<string, unknown>, path: string): void {
  requireString(order, 'id', `${path}.id`);
  requireNullableString(order, 'orderNumber', `${path}.orderNumber`);
  requireEnum(
    order,
    'status',
    ['DRAFT', 'SUBMITTED', 'PARTIAL', 'RECEIVED', 'CLOSED', 'CANCELLED'],
    `${path}.status`,
  );
  requireString(order, 'supplierName', `${path}.supplierName`);
  requireNumber(order, 'itemCount', `${path}.itemCount`);
  requireNumber(order, 'totalValue', `${path}.totalValue`);
  requireString(order, 'createdAt', `${path}.createdAt`);
}

function validateSupplierRestock(supplier: Record<string, unknown>, path: string): void {
  for (const key of ['supplierId', 'supplierName']) requireString(supplier, key, `${path}.${key}`);
  for (const key of ['productCount', 'criticalCount', 'lowCount', 'totalReorderUnits']) {
    requireNumber(supplier, key, `${path}.${key}`);
  }
  for (const key of ['totalCost', 'freeFreightMinimum', 'targetPOValue']) {
    requireNullableNumber(supplier, key, `${path}.${key}`);
  }
  requireEnum(supplier, 'valueStatus', ['COMPLETE', 'PARTIAL', 'UNAVAILABLE'], `${path}.valueStatus`);
  requireNumber(supplier, 'unpricedProductCount', `${path}.unpricedProductCount`);
}

function validateCoverage(value: Record<string, unknown>): void {
  const orders = requireRecord(value, 'ordersCoverage');
  requireEnum(orders, 'status', ['complete', 'incomplete', 'empty'], 'ordersCoverage.status');
  for (const key of [
    'coveredStart', 'coveredEnd', 'latestPurchaseDateOverall', 'latestPurchaseDateInRange',
    'lastSuccessfulOrdersSyncAt', 'failureMessage',
  ]) requireNullableString(orders, key, `ordersCoverage.${key}`);
  for (const key of ['orderCountInRange', 'skuCountInRange']) {
    requireNumber(orders, key, `ordersCoverage.${key}`);
  }

  const economics = requireRecord(value, 'economicsCoverage');
  requireEnum(economics, 'status', ['complete', 'incomplete', 'empty'], 'economicsCoverage.status');
  for (const key of ['coveredStart', 'coveredEnd', 'lastSuccessfulEconomicsSyncAt', 'failureReason']) {
    requireNullableString(economics, key, `economicsCoverage.${key}`);
  }
  requireNumber(economics, 'pendingWindowCount', 'economicsCoverage.pendingWindowCount');
}

function validateFinancialMetric(metric: Record<string, unknown>, path: string): void {
  requireNullableNumber(metric, 'value', `${path}.value`);
  requireEnum(metric, 'availability', ['exact', 'partial', 'unavailable'], `${path}.availability`);
  requireEnum(metric, 'source', ['amazon', 'seller_costs', 'operating_expenses', 'derived'], `${path}.source`);
  requireNumber(metric, 'coveredDays', `${path}.coveredDays`);
  requireNumber(metric, 'totalDays', `${path}.totalDays`);
  requireNullableString(metric, 'throughDate', `${path}.throughDate`);
  requireNullableEnum(metric, 'reasonCode', [
    'incomplete_amazon_coverage', 'missing_cost_data', 'expense_coverage_missing',
    'expense_coverage_partial', 'metric_unavailable',
  ], `${path}.reasonCode`);
}

function validateFinancialSummary(financial: Record<string, unknown>): void {
  requireEnum(financial, 'salesAndTrafficStatus', ['ready', 'pending'], 'financialSummary.salesAndTrafficStatus');
  for (const key of [
    'grossSales', 'netProceeds', 'contributionProfit', 'operatingExpenseTotal',
    'operatingProfit', 'profit', 'reconciliationVariance', 'margin', 'roi', 'refunds', 'fees',
    'ads', 'costOfGoodsSold', 'shippingToAmazonCost', 'otherOffAmazonCosts', 'sellerCost',
  ]) requireNullableNumber(financial, key, `financialSummary.${key}`);
  requireNullableEnum(
    financial,
    'profitBasis',
    ['amazon_net_proceeds', 'zeroth_components'],
    'financialSummary.profitBasis',
  );
  for (const key of [
    'hasFinancialData', 'hasProfitabilityData', 'contributionProfitIsEstimated',
    'operatingProfitIsEstimated', 'profitIsEstimated', 'lineItemsArePartial',
  ]) requireBoolean(financial, key, `financialSummary.${key}`);
  requireEnum(financial, 'financialBasis', ['profit_analytics'], 'financialSummary.financialBasis');
  requireEnum(financial, 'financialSource', ['profit_analytics'], 'financialSummary.financialSource');
  const availabilityValues = [
    'ready', 'pending_sync', 'permission_required', 'reauthorization_required', 'sync_error',
    'incomplete_coverage', 'missing_cost_data',
  ];
  requireEnum(
    financial,
    'economicsAvailability',
    availabilityValues.filter((value) => value !== 'missing_cost_data'),
    'financialSummary.economicsAvailability',
  );
  requireEnum(financial, 'profitabilityAvailability', availabilityValues, 'financialSummary.profitabilityAvailability');
  requireEnum(financial, 'availabilityStatus', availabilityValues, 'financialSummary.availabilityStatus');
  requireNullableEnum(
    financial,
    'unavailableReason',
    availabilityValues.filter((value) => value !== 'ready'),
    'financialSummary.unavailableReason',
  );
  for (const key of ['financialRangeStart', 'financialRangeEndExclusive', 'financialAsOf']) {
    requireNullableString(financial, key, `financialSummary.${key}`);
  }
  const profitabilityCoverage = requireRecord(
    financial,
    'profitabilityCoverage',
    'financialSummary.profitabilityCoverage',
  );
  requireEnum(profitabilityCoverage, 'status', ['none', 'partial', 'complete'], 'financialSummary.profitabilityCoverage.status');
  for (const key of ['coveredStart', 'coveredEnd', 'latestAvailableDate']) {
    requireNullableString(profitabilityCoverage, key, `financialSummary.profitabilityCoverage.${key}`);
  }
  requireEnum(financial, 'costBasisSource', ['po_invoice', 'product_supplier_cost', 'amazon_seller_cost', 'mixed', 'none'], 'financialSummary.costBasisSource');
  requireEnum(financial, 'costBasisCoverage', ['none', 'partial', 'complete'], 'financialSummary.costBasisCoverage');
  requireEnum(financial, 'costCoverage', ['none', 'partial', 'complete'], 'financialSummary.costCoverage');

  const expenseCoverage = requireRecord(financial, 'expenseCoverage', 'financialSummary.expenseCoverage');
  requireEnum(expenseCoverage, 'status', ['none', 'partial', 'complete'], 'financialSummary.expenseCoverage.status');
  requireNumber(expenseCoverage, 'coveredDays', 'financialSummary.expenseCoverage.coveredDays');
  requireNumber(expenseCoverage, 'totalDays', 'financialSummary.expenseCoverage.totalDays');
  requireNullableString(expenseCoverage, 'coveredStart', 'financialSummary.expenseCoverage.coveredStart');
  requireNullableString(expenseCoverage, 'coveredEndExclusive', 'financialSummary.expenseCoverage.coveredEndExclusive');
  for (const key of [
    'coveredSkuCount', 'totalSkuCount', 'coveredUnitCount', 'totalUnitCount',
    'missingImportedCostSkuCount',
  ]) requireNumber(financial, key, `financialSummary.${key}`);

  if (financial.metrics !== undefined) {
    const metrics = isRecord(financial.metrics)
      ? financial.metrics
      : fail('financialSummary.metrics', 'is not an object');
    for (const key of [
      'amazonNetProceeds', 'contributionProfit', 'operatingExpenses', 'operatingProfit',
      'netAmazonFees', 'returnsAndRefunds', 'advertising', 'costOfGoodsSold',
    ]) {
      validateFinancialMetric(requireRecord(metrics, key, `financialSummary.metrics.${key}`), `financialSummary.metrics.${key}`);
    }
  }
}

function validateRestockRecommendation(value: Record<string, unknown>): void {
  const recommendation = requireRecord(value, 'restockRecommendation');
  requireEnum(recommendation, 'recommendationState', ['CURRENT', 'LAST_PUBLISHED', 'UNAVAILABLE'], 'restockRecommendation.recommendationState');
  requireBoolean(recommendation, 'recommendationActionable', 'restockRecommendation.recommendationActionable');
  requireEnum(recommendation, 'orderHistoryState', ['READY', 'REPAIRING'], 'restockRecommendation.orderHistoryState');
  requireNullableString(recommendation, 'orderHistoryAsOf', 'restockRecommendation.orderHistoryAsOf');
  requireNullableString(recommendation, 'dataAsOf', 'restockRecommendation.dataAsOf');
}

export function validateDashboardData(value: unknown): value is DashboardData {
  if (!isRecord(value)) return false;

  try {
    const widgets = requireStringArray(value, 'availableWidgets');
    if (!widgets.every((widget) => (DASHBOARD_WIDGET_IDS as readonly string[]).includes(widget))) {
      fail('availableWidgets', 'contains an invalid widget');
    }
    validateRange(requireRecord(value, 'range'));
    validateKpis(requireRecord(value, 'kpis'));
    validateCharts(requireRecord(value, 'charts'));
    requireRecordArray(value, 'topProducts', validateTopProduct);
    requireRecordArray(value, 'restockAlerts', validateRestockAlert);
    requireRecordArray(value, 'topRestockProducts', validateTopRestockProduct);
    requireRecordArray(value, 'recentPurchaseOrders', validatePurchaseOrder);
    requireRecordArray(value, 'supplierRestock', validateSupplierRestock);
    validateCoverage(value);
    requireEnum(value, 'salesActivitySource', ['preset_listing_snapshot', 'canonical_range', 'none']);
    validateFinancialSummary(requireRecord(value, 'financialSummary'));
    validateRestockRecommendation(value);
    return true;
  } catch (error) {
    if (error instanceof DashboardDataContractError) return false;
    throw error;
  }
}

export function parseDashboardApiResponse(value: unknown): DashboardData {
  if (!isRecord(value)) {
    throw new DashboardDataContractError('response envelope is missing');
  }
  if (value.schemaVersion !== DASHBOARD_SCHEMA_VERSION) {
    throw new DashboardDataContractError('schema version does not match this frontend');
  }
  if (!validateDashboardData(value.data)) {
    throw new DashboardDataContractError('required dashboard fields are missing or invalid');
  }
  return value.data;
}
